import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';

import { cn } from '@epoch-ui/lib/utils';
import { buttonVariants } from '@epoch-ui/components/Button';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function CalendarHead(): JSX.Element {
  return (
    <thead className="tw-flex">
      <tr className="tw-flex tw-text-center">
        <th className="tw-w-[52px] tw-px-[16px] tw-py-[17px]">S</th>
        <th className="tw-w-[52px] tw-px-[16px] tw-py-[17px]">M</th>
        <th className="tw-w-[52px] tw-px-[16px] tw-py-[17px]">T</th>
        <th className="tw-w-[52px] tw-px-[16px] tw-py-[17px]">W</th>
        <th className="tw-w-[52px] tw-px-[16px] tw-py-[17px]">T</th>
        <th className="tw-w-[52px] tw-px-[16px] tw-py-[17px]">F</th>
        <th className="tw-w-[52px] tw-px-[16px] tw-py-[17px]">S</th>
      </tr>
    </thead>
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps): JSX.Element {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('tw-p-3', className)}
      classNames={{
        months: 'tw-flex tw-flex-col sm:tw-flex-row tw-space-y-4 sm:tw-space-x-4 sm:tw-space-y-0',
        month: 'tw-space-y-4',
        caption: 'tw-flex tw-justify-center tw-pt-1 tw-relative tw-items-center',
        caption_label: 'tw-text-sm tw-font-medium',
        nav: 'tw-space-x-1 tw-flex tw-items-center',
        nav_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'tw-h-6 tw-w-6 tw-bg-transparent tw-p-0'
        ),
        nav_button_previous: 'tw-absolute tw-left-1',
        nav_button_next: 'tw-absolute tw-right-1',
        table: 'tw-w-full tw-border-collapse tw-space-y-1',
        row: 'tw-flex tw-w-full tw-mt-[1px]',
        cell: 'tw-h-[52px] tw-w-[52px] tw-text-center tw-text-sm tw-p-0 tw-relative [&:has([aria-selected].day-range-end)]:tw-rounded-r-md [&:has([aria-selected].day-outside)]:tw-bg-accent/50 [&:has([aria-selected])]:tw-bg-accent first:[&:has([aria-selected])]:tw-rounded-l-md last:[&:has([aria-selected])]:tw-rounded-r-md focus-within:tw-relative focus-within:tw-z-20',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'tw-h-[52px] tw-w-[52px] tw-p-0 tw-font-normal aria-selected:tw-opacity-100'
        ),
        day_range_end: 'tw-day-range-end',
        // TODO: figure out how to get the background to cover half of the container when using circle day selections
        // day_range_end: 'tw-day-range-end !tw-rounded-[100px]',
        // day_range_start: '!tw-rounded-[100px]',
        day_selected:
          'tw-bg-primary tw-text-primary-foreground hover:tw-bg-primary hover:tw-text-primary-foreground focus:tw-bg-primary focus:tw-text-primary-foreground',
        day_today: 'tw-bg-accent tw-text-accent-foreground',
        day_outside: 'tw-day-outside',
        day_disabled: 'tw-text-muted-foreground tw-opacity-50',
        day_range_middle:
          'aria-selected:tw-bg-light-blue-badge aria-selected:tw-text-accent-foreground',
        day_hidden: 'tw-invisible',
        ...classNames
      }}
      components={{
        Head: () => <CalendarHead />,
        IconLeft: (): JSX.Element => <ChevronLeft className="tw-h-4 tw-w-4" />,
        IconRight: (): JSX.Element => <ChevronRight className="tw-h-4 tw-w-4" />
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
